import { Conditions } from '@/api/enums';
import { AppBreadCrumbTemplate } from '@/app/AppBreadCrumbTemplate';
import { Account } from '@/client/accounts';
import { HubspotProperty, HubspotValue } from '@/client/hubspot';
import { Actions, Subjects, SystemRoles } from '@/client/users';
import {
  LoadingStatuses,
  RedirectPaths,
  RedirectPathsEnum,
} from '@/common/constants';
import { TranslationFunctionType } from '@/common/types';
import { BlockEmailsBanner } from '@/components/block-emails';
import { CampaignList, TryForFreePhishing } from '@/components/campaigns';
import { CampaignRecommendationModal } from '@/components/campaigns/recommendation';
import { WhitelistingBanner } from '@/components/campaigns/simulations';
import { WhitelistingModal } from '@/components/campaigns/simulations/modals';
import { CampaignsReports } from '@/components/dashboards/admin';
import { useAccount, useCampaigns, useCampaignsSummary } from '@/hooks/query';
import { useAppSelector } from '@/hooks/store';
import { useLanguageFailureToasts } from '@/hooks/useLanguageFailureToasts';
import { usePermission } from '@/hooks/usePermission';

import { selectCurrentAccount } from '@/store/features/account';
import { selectCurrentUserState } from '@/store/features/users';
import { AppBreadCrumb } from '@/ui/breadcrumb';
import { AppButton } from '@/ui/buttons';
import { FlexContainer } from '@/ui/styled-ui';
import { isDemoRestrictedMode, showBlockEmailsMessage } from '@/utils/helpers';
import { hubspotTrack } from '@/utils/hubspot';
import { MenuItem } from 'primereact/menuitem';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const getBreadcrumbs = (
  account: Account,
  t: TranslationFunctionType,
): MenuItem[] => {
  return [
    {
      label: account?.name,
      url: !account?.isSystem
        ? RedirectPaths[RedirectPathsEnum.ACCOUNT](account?.id)
        : RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](account?.id),
      template: AppBreadCrumbTemplate,
    },
    {
      label: t('campaigns'),
      url: RedirectPaths[RedirectPathsEnum.CAMPAIGNS](),
      template: AppBreadCrumbTemplate,
    },
  ];
};

export const CampaignsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const account = useAppSelector(selectCurrentAccount);
  const hasBlockEmailsBanner =
    showBlockEmailsMessage(account) &&
    !!account?.settings?.phishingFollowUpEnabled;
  const { can } = usePermission();
  const { user } = useAppSelector(selectCurrentUserState);
  const [showIndividualCampaigns, setShowIndividualCampaigns] = useState(false);
  const [recommendCampaignModalVisible, setRecommendCampaignModalVisible] =
    useState(false);
  const [isWhitelistingModalOpen, setIsWhitelistingModalOpen] = useState(false);
  const isDemoAccount = isDemoRestrictedMode(account, user);
  const { account: freshAccount } = useAccount({
    accountId: account?.id,
    enabled: !!account?.id && !account?.isSystem,
  });
  useLanguageFailureToasts(freshAccount);
  const isPhishingSenderWhitelisted =
    freshAccount?.isPhishingSenderWhitelisted ??
    account?.isPhishingSenderWhitelisted;

  const { isLoading: areCampaignsLoading, campaigns } = useCampaigns({
    withTemplates: 0,
    take: 0,
    skip: 0,
    enabled: !account?.isSystem,
    filters: [
      {
        field: 'account',
        condition: Conditions.EQUAL,
        value: (account as Account)?.id,
      },
    ],
  });

  const {
    isLoading: isLoadingAutoPhishingCampaigns,
    campaigns: autoPhishingCampaigns,
  } = useCampaigns({
    withTemplates: 0,
    take: 0,
    skip: 0,
    enabled: !account?.isSystem,
    filters: [
      {
        field: 'account',
        condition: Conditions.EQUAL,
        value: (account as Account)?.id,
      },
      {
        field: 'isAutoPhishing',
        condition: Conditions.EQUAL,
        value: true,
      },
    ],
  });

  const { isLoading: isLoadingSummary, campaignsSummary } = useCampaignsSummary(
    {
      accountId: account?.id,
      showIndividualCampaigns: showIndividualCampaigns ? 1 : 0,
    },
  );

  useEffect(() => {
    hubspotTrack(HubspotProperty.PHISHING_CAMPAIGN_VIEW, HubspotValue.YES);
  }, []);

  return (
    <FlexContainer
      direction="column"
      align="flex-start"
      justify="flex-start"
      style={{ minHeight: '100%' }}
    >
      {hasBlockEmailsBanner && <BlockEmailsBanner />}
      <AppBreadCrumb model={getBreadcrumbs(account as Account, t)} />

      {!campaigns?.count && !user?.account?.isSystem ? (
        <TryForFreePhishing isLoading={areCampaignsLoading} />
      ) : (
        <>
          <FlexContainer
            align="baseline"
            justify="space-between"
            className="mt-2"
          >
            <h1 className="mb-0">{t('campaigns')}</h1>
            <div className="flex gap-2">
              {user?.role.code &&
                [SystemRoles.SUPER_ADMIN, SystemRoles.DEVELOPER].includes(
                  user.role.code,
                ) && (
                  <>
                    <CampaignRecommendationModal
                      visible={recommendCampaignModalVisible}
                      onHide={() => setRecommendCampaignModalVisible(false)}
                    />
                    <AppButton
                      label={t('campaign.recommendation')}
                      severity="warning"
                      onClick={() => setRecommendCampaignModalVisible(true)}
                    />
                  </>
                )}
              {!account?.isSystem && (
                <AppButton
                  label={t('campaigns.catalogue')}
                  severity="secondary"
                  state={
                    areCampaignsLoading
                      ? LoadingStatuses.LOADING
                      : LoadingStatuses.IDLE
                  }
                  onClick={() =>
                    navigate(
                      RedirectPaths[RedirectPathsEnum.CAMPAIGNS_CATALOGUE_V2](),
                    )
                  }
                />
              )}
            </div>
          </FlexContainer>
          {!isPhishingSenderWhitelisted &&
            !account?.isSystem &&
            !(
              autoPhishingCampaigns?.count === 0 &&
              !isLoadingAutoPhishingCampaigns
            ) && (
              <WhitelistingBanner
                id="campaigns-start-whitelisting"
                onStartWhitelisting={
                  isDemoAccount
                    ? undefined
                    : () => setIsWhitelistingModalOpen(true)
                }
                className="mt-3"
              />
            )}
          {can(Actions.READ, Subjects.ACCOUNT_CAMPAIGNS_SUMMARY) &&
            !isLoadingSummary &&
            campaignsSummary &&
            !account?.isSystem &&
            account && (
              <CampaignsReports
                campaignsSummary={campaignsSummary}
                skipADConfigFetch
                isLoading={isLoadingSummary}
                t={t}
                className="mt-4 w-full"
                isTealColor
                useEmailsSentDenominator
              />
            )}

          <CampaignList
            showIndividualCampaigns={showIndividualCampaigns}
            onShowIndividualCampaignsChange={setShowIndividualCampaigns}
          />
        </>
      )}
      {account?.id && (
        <WhitelistingModal
          isOpen={isWhitelistingModalOpen}
          onClose={() => setIsWhitelistingModalOpen(false)}
          accountId={account.id}
        />
      )}
    </FlexContainer>
  );
};
